import { Inject, Injectable, Optional, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BehaviorSubject, Observable, Subscription, throwError } from 'rxjs';

import { AlloyWrapper, Init } from './alloy-wrapper.service';
import { AlloyConfig } from './alloy-config.const';
import { OidSessionService, ThemeService } from '@cudirect/shared-ui-library';
import { LenderService } from '@shared/service/lender/lender.service';
import { AdminPortalFeaturesService } from '@workflow/ecp-shared/service/user/admin-portal-features.service';
import { StateManagementService } from '@workflow/service/state-management/state-management.service';
import { environment } from 'environments/environment';
import { StatefulService } from '@app/service/stateful.service';

// #region tracking page list
// these are pages we support tracking on - todo: would love a better way to manage this for workflow changes.
const validTrackingPages: string[] = [
  'oao-application-fields',
  'oao-application-residential-history',
  'action-poll-appid',
  'oao-employment-history',
  'oao-application-fields-co-applicant',
  'oao-application-residential-history-co-applicant',
  'oao-employment-history-co-applicant',
  'oao-application-fields-additional-co-app',
  'oao-application-residential-history-additional-co-app',
  'oao-employment-history-additional-co-app',
  'oao-application-checkpoint',
  'oao-additional-accounts',
  'oao-beneficiary',
  'oao-additional-information',
  'oao-requested-document',
  'oao-identity',
  'oao-application-fields-youth',
  'oao-application-residential-history',
  'action-poll-appid',
  'oao-employment-history-youth',
  'oao-application-fields-co-applicant',
  'oao-application-residential-history-co-applicant',
  'oao-employment-history-co-applicant',
  'oao-application-fields-additional-co-app',
  'oao-application-residential-history-additional-co-app',
  'oao-employment-history-additional-co-app',
  'oao-application-checkpoint',
  'oao-additional-accounts',
  'oao-beneficiary',
  'oao-additional-information',
  'oao-requested-document',
  'oao-business-info',
  'oao-business-application-fields-#',
  'oao-responsible-party-resident-#',
  'action-poll-appid',
  'oao-responsible-party-employment-history-#',
  'oao-beneficial-owner-#',
  'oao-beneficial-resident-#',
  'oao-beneficial-employment-history-#',
  'oao-business-auth-signers-application-fields-#',
  'oao-auth-signer-resident-#',
  'oao-application-checkpoint-business',
  'oao-additional-accounts-business',
  'oao-beneficiary-business',
  'oao-additional-information-business',
  'oao-bank-secrecy-act',
  'oao-requested-document',
  'oao-identity-business',
  'ola-secure-loan-personal-info',
  'ola-secure-loan-co-applicant-#',
  'ola-consumer-residential',
  'ola-consumer-residential-co-borrower-#',
  'ola-action-poll-appId',
  'ola-employment-history',
  'ola-employment-history-co-borrower-#',
  'ola-references',
  'ola-references-co-borrower-#',
  'ola-consumer-additional-info'
];
// #endregion tracking page list

@Injectable({
  providedIn: 'root'
})
export class AlloyService {
  /** Used locally only for */
  private bypass = false;
  private initParams: Init = {
    production: false,
    key: undefined
  };
  private readonly statefulService = inject(StatefulService);

  constructor(
    private readonly alloy: AlloyWrapper,
    private readonly themeSvc: ThemeService,
    private readonly lenderService: LenderService,
    private readonly adminPortalFeaturesService: AdminPortalFeaturesService,
    private readonly stateManager: StateManagementService,
    private readonly userTimeoutService: OidSessionService,
    @Inject(DOCUMENT) private readonly document: Document,
    @Optional() private readonly config: AlloyConfig
  ) {
    if (this.config) {
      this.initParams = Object.assign(this.initParams, this.config.initParams);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (!environment.production && (environment as any).alloySkip) {
      // don't allow skipping in anywhere but local no matter what.
      this.bypass = true;
    }
  }

  public readonly isInitialized = new BehaviorSubject<boolean>(false);
  public readonly inProgress = new BehaviorSubject<boolean>(false);

  /**
   * This centralizes checking if alloy is enabled.
   * @returns An object with whether or not alloy is enabled, and if a terminal status has been reached, what that is.
   */
  public isAlloyEnabled(): { enabled: boolean; state?: 'reject' | 'approve' } {
    if (this.bypass) return { enabled: false };
    const adminPortalFeatures = this.adminPortalFeaturesService?.config;
    const alloyConfig = this.lenderService.lenderData.alloySettings;
    const isEnabled = alloyConfig?.isEnabled === true;
    if (!isEnabled) {
      return { enabled: false };
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const apiState = this.stateManager.get<any>('api');
    const oaoFlow = apiState?.oaoMemberInformation?.membership?.membershipID;

    if (oaoFlow) {
      // block when already rejected
      if (
        apiState?.journeyInfo?.alloyStatus?.toLowerCase() === 'completed' &&
        apiState?.journeyInfo?.alloyOutcome?.toLowerCase() === 'denied'
      ) {
        return { enabled: true, state: 'reject' };
      }

      const adminMembership = adminPortalFeatures?.oaoConfiguration?.memberships?.find((t) => t.membershipID === oaoFlow);
      let oaoAlloyJourneyId = adminMembership?.alloyJourneyId || 'none';
      if (oaoAlloyJourneyId === 'null') {
        oaoAlloyJourneyId = 'none';
      }

      // if it can't find a membership (weird) or the journey is none
      if (!adminMembership || !oaoAlloyJourneyId || oaoAlloyJourneyId?.toLowerCase() === 'none') {
        return { enabled: false };
      }

      return { enabled: true };
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const olaState = this.stateManager.get<any>('ola');
    if (
      olaState?.journeyInfo?.alloyStatus?.toLowerCase() === 'completed' &&
      olaState?.journeyInfo?.alloyOutcome?.toLowerCase() === 'denied'
    ) {
      return { enabled: true, state: 'reject' };
    }

    const selectedLoanProduct = olaState?.olaApplicationInformation?.loanProductName;
    const selectedLoanGroup = olaState?.olaApplicationInformation?.loanGroupName;
    const adminLoanObject = adminPortalFeatures?.olaConfiguration?.consumerLoanGroups
      ?.find((l) => l.loanGroup === selectedLoanGroup)
      ?.loanProducts?.find((p) => p.productName === selectedLoanProduct);

    let olaAlloyJourneyId = adminLoanObject?.alloyJourneyId || 'none';
    if (olaAlloyJourneyId === 'null') {
      olaAlloyJourneyId = 'none';
    }

    // if it can't find a loan product (weird) or the journey is none
    if (!adminLoanObject || !olaAlloyJourneyId || olaAlloyJourneyId?.toLowerCase() === 'none') {
      return { enabled: false };
    }

    return { enabled: true };
  }

  /**
   * Gets a value indicating if red path should block
   * @returns true if denied is a hard block, otherwise false
   */
  public isDeniedBlocking(): boolean {
    const adminPortalFeatures = this.adminPortalFeaturesService?.config;
    const alloyConfig = this.lenderService.lenderData.alloySettings;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const apiState = this.stateManager.get<any>('api');
    const oaoFlow = apiState?.oaoMemberInformation?.membership?.membershipID;

    if (oaoFlow) {
      const adminMembership = adminPortalFeatures?.oaoConfiguration?.memberships?.find((t) => t.membershipID === oaoFlow);
      const oaoAlloyJourneyId = adminMembership?.alloyJourneyId || 'none';
      return alloyConfig.journeys.find((j) => j.id === oaoAlloyJourneyId)?.isDeniedBlocking || false;
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const olaState = this.stateManager.get<any>('ola');
      const selectedLoanProduct = olaState?.olaApplicationInformation?.loanProductName;
      const selectedLoanGroup = olaState?.olaApplicationInformation?.loanGroupName;
      const adminLoanObject = adminPortalFeatures?.olaConfiguration?.consumerLoanGroups
        ?.find((l) => l.loanGroup === selectedLoanGroup)
        ?.loanProducts?.find((p) => p.productName === selectedLoanProduct);

      const olaAlloyJourneyId = adminLoanObject?.alloyJourneyId || 'none';
      return alloyConfig.journeys.find((j) => j.id === olaAlloyJourneyId)?.isDeniedBlocking || false;
    }
  }

  /**
   * Checks if we are in a valid state with alloy - not yet performed or not denied.
   * @returns True if valid, otherwise false
   */
  public isAlloyValid(): boolean {
    // if not enabled, then it is valid because not used
    if (!this.isAlloyEnabled().enabled) {
      return true;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const apiState = this.stateManager.get<any>('api');
    const oaoFlow = apiState?.oaoMemberInformation?.membership?.membershipID;
    const adminPortalFeatures = this.adminPortalFeaturesService?.config;
    if (oaoFlow) {
      let selectedMembershipId: string | number = oaoFlow;
      if (selectedMembershipId && typeof selectedMembershipId === 'string') {
        selectedMembershipId = parseInt(selectedMembershipId, 10);
      }
      const adminMembership = adminPortalFeatures?.oaoConfiguration?.memberships?.find(
        (t) => t.membershipID === (selectedMembershipId as number)
      );
      const enableMembershipAlloy = adminMembership && typeof adminMembership.alloyJourneyId === 'string';
      // check if actually in a red path, or not yet verified
      const outcome = apiState.journeyInfo.alloyOutcome;
      if (enableMembershipAlloy && outcome?.toLowerCase() === 'denied') {
        return false;
      }
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const olaState = this.stateManager.get<any>('ola');
      const selectedLoanProduct = olaState.olaApplicationInformation.loanProductName;
      const selectedLoanGroup = olaState.olaApplicationInformation.loanGroupName;
      const adminLoanObject = adminPortalFeatures?.olaConfiguration?.consumerLoanGroups
        ?.find((l) => l.loanGroup === selectedLoanGroup)
        ?.loanProducts?.find((p) => p.productName === selectedLoanProduct);
      const enableLendingAlloy = adminLoanObject && typeof adminLoanObject.alloyJourneyId === 'string';
      // check if actually in a red path
      const outcome = olaState.journeyInfo.alloyOutcome;
      if (enableLendingAlloy && outcome?.toLowerCase() === 'denied') {
        return false;
      }
    }
    return true;
  }
  /**
   * Centralizes the retrieval of properties for alloy into a single method.
   * @returns The keys from the configuration for alloy integration
   */
  public getAlloyInitArgs(mode: string): { sdkKey: string; journeyToken: string; sourcePublicToken?: string } {
    let sdkKey: string;
    let journeyToken: string;
    let socureToken: string;
    const alloySettings = this.lenderService.lenderData.alloySettings;
    if (mode.toLowerCase() === 'api') {
      // note: this parse int workaround is in case the value is not being stored as an int - stateful value is problematic
      const selectedMembershipId = parseInt(
        this.statefulService.wrap('api:oaoMemberInformation/membership/membershipID').value?.toString(),
        10
      );
      if (!selectedMembershipId || Number.isNaN(selectedMembershipId)) {
        // rare edge case if signed out or direct call - this is null
        return {} as { sdkKey: string; journeyToken: string; sourcePublicToken?: string };
      }
      const membership = this.adminPortalFeaturesService.config?.oaoConfiguration?.memberships?.find(
        (m) => m.membershipID === selectedMembershipId
      );
      const journeyId = membership.alloyJourneyId;
      const journey = alloySettings?.journeys?.find((j) => j.id === journeyId);
      sdkKey = journey?.sdkKey;
      journeyToken = journey?.journeyToken;
      if (journey?.useSocure) {
        socureToken = alloySettings?.socurePublicToken;
      }
    } else {
      const selectedLoanProduct = this.statefulService.wrap('ola:olaApplicationInformation/loanProductName').valueString;
      const selectedLoanGroup = this.statefulService.wrap('ola:olaApplicationInformation/loanGroupName').valueString;
      const loan = this.adminPortalFeaturesService.config.olaConfiguration?.consumerLoanGroups
        ?.find((l) => l.loanGroup === selectedLoanGroup)
        ?.loanProducts?.find((p) => p.productName === selectedLoanProduct);
      const journeyId = loan?.alloyJourneyId;
      const journey = alloySettings?.journeys?.find((j) => j.id === journeyId);
      sdkKey = journey?.sdkKey;
      journeyToken = journey?.journeyToken;
      if (journey?.useSocure) {
        socureToken = alloySettings?.socurePublicToken;
      }
    }
    const results: { sdkKey: string; journeyToken: string; sourcePublicToken?: string } = { sdkKey, journeyToken };
    if (socureToken) {
      // we don't even want this on the object if not there
      results.sourcePublicToken = socureToken;
    }
    return results;
  }

  /**
   * If alloy is enabled for the application, and we are on a page after econsent,
   * this will initialize alloy in the case of tracking tools like NeuroID or ThreatMetrix
   * to capture behavior data and assess human-ness...
   * @param pageId The current page id
   * @returns Empty blankness
   */
  public trackIfEnabled(pageId: string): void {
    if (validTrackingPages.some((p) => pageId.includes(p)) && this.isAlloyEnabled().enabled) {
      // we tracking
      const mode = pageId.startsWith('ola') ? 'ola' : 'api';
      const inputs = this.getAlloyInitArgs(mode);
      if (!inputs.sdkKey || !inputs.journeyToken) {
        return; // alloy not enabled for this journey DO NOT TRACK :)
      }
      const tokens: {
        key: string;
        journeyToken: string;
        journeyApplicationToken?: string;
        socurePublicToken?: string;
        isNext?: boolean;
      } = {
        key: inputs.sdkKey,
        journeyToken: inputs.journeyToken
      };
      if (typeof inputs.sourcePublicToken === 'string') {
        tokens.socurePublicToken = inputs.sourcePublicToken;
        tokens.isNext = true;
      }
      // tracking can ignore the return value, that will be handled in verify before step up
      this.init(tokens).subscribe((props) => {
        if (props.iovationBlackboxId) {
          this.trackWithIovation();
        }
      });
    } else {
      this.stopTrackingWithIovation();
    }
  }

  /**
   * This method initializes Alloy with the specified context. This should be called after the
   * journey application has been started if step up is required.
   * @param tokens Provided by the selected flow, if the flow has Alloy enabled, it should provide an sdkKey and journeyToken.
   * @returns An observable for when initialization has completed.
   */
  public init(tokens: {
    key: string;
    journeyToken: string;
    journeyApplicationToken?: string;
    socurePublicToken?: string;
    isNext?: boolean;
  }): Observable<{
    neuroUserId?: string;
    neuroSiteId?: string;
    socureDeviceId?: string;
    threatMetrixSessionId?: string;
    iovationBlackboxId?: string;
  }> {
    this.inProgress.next(true);
    const colors = this.themeSvc.creditUnionColors;
    const colorInit: Init = {
      customStyle: {
        ...(this.initParams.customStyle || {}),
        theme: {
          ...(this.initParams.customStyle?.theme || {}),
          primaryColor: colors.Primary
        }
      }
    } as Init;
    const config = Object.assign({}, this.initParams, tokens, colorInit);
    this.checkAndSetIfSandboxMode(config);
    return new Observable<{
      neuroUserId?: string;
      neuroSiteId?: string;
      socureDeviceId?: string;
      threatMetrixSessionId?: string;
    }>((sub) => {
      this.alloy.init(config).then(
        (data) => {
          const result: {
            neuroUserId?: string;
            neuroSiteId?: string;
            socureDeviceId?: string;
            threatMetrixSessionId?: string;
            iovationBlackboxId?: string;
          } = {};
          if (data) {
            if (tokens.socurePublicToken) {
              // we only want this if we provided a key
              result.socureDeviceId = data.socureDeviceId;
            }
            // these we want if they exist
            if (data.neuroIdSiteId) {
              result.neuroSiteId = data.neuroIdSiteId;
            }
            if (data.neuroUserId) {
              result.neuroUserId = data.neuroUserId;
            }
            if (data.threatmetrixSessionId) {
              result.threatMetrixSessionId = data.threatmetrixSessionId;
            }
            if (data.iovationBlackboxId) {
              result.iovationBlackboxId = data.iovationBlackboxId;
            }
          }
          this.isInitialized.next(true);
          sub.next(result);
          sub.complete();
        },
        (err) => {
          sub.error(err);
        }
      );
    });
  }

  /**
   * This method triggers the verification modal triggered via requiring step up from the journey creation.
   * Only needs to be called if we get a step-up request.
   * @see AlloyVerificationResult.requireStepUp for whether or not to call this..
   * @returns The observable that processes the step validation modal.
   */
  public verifyApplication(): Observable<AlloyVerificationResult> {
    if (!this.isInitialized.value) {
      return throwError(() => new Error('Alloy is uninitialized. Please call init first before starting an application.'));
    }
    this.inProgress.next(true);
    return new Observable<AlloyVerificationResult>((sub) => {
      if (this.config.stepUpAnchor && this.config.anchorIsDedicated) {
        const anchorEl = this.document.getElementById(this.config.stepUpAnchor);
        anchorEl.classList.remove('hidden');
        this.document.body.classList.add('body-modal-active');
      }
      // this is not valid linting, I need the value in the callback of open but
      // I need to create after open successfully is called so I don't end up with
      // a dangling subscription
      // eslint-disable-next-line prefer-const
      let timerSub: Subscription;
      this.alloy.open((resp) => {
        // handle closed
        if (resp.sdk.sdkEvent === 'exit') {
          sub.error({
            event: 'exit',
            message: 'The step-up window was closed.'
          });
          sub.complete();
        }
        // handle logic
        if (
          resp.status.toLowerCase() === 'completed' &&
          (resp.complete_outcome?.toLowerCase() === 'approved' || resp.complete_outcome?.toLowerCase() === 'denied')
        ) {
          this.inProgress.next(false);
        }
        const verified = resp.status.toLowerCase() === 'completed' && resp.complete_outcome?.toLowerCase() === 'approved';
        const rejected = resp.status.toLowerCase() === 'completed' && resp.complete_outcome?.toLowerCase() === 'denied';
        let action: AlloyStatus;
        if (verified) {
          action = AlloyStatus.Verified;
        } else if (rejected) {
          action = AlloyStatus.Rejected;
        } else {
          action = AlloyStatus.ManualReview;
        }
        sub.next({
          status: resp.status.toLowerCase(),
          journeyApplicationId: resp.journey_application_token,
          outcome: resp.complete_outcome?.toLowerCase(),
          journeyStatus: resp.journey_application_status?.toLowerCase(),
          stepUpRequired: false,
          actionRequired: action,
          applicationId: '',
          applicantName: ''
        });
        sub.complete();
        if (this.config.stepUpAnchor && this.config.anchorIsDedicated) {
          const anchorEl = this.document.getElementById(this.config.stepUpAnchor);
          anchorEl.classList.add('hidden');
          this.document.body.classList.remove('body-modal-active');
        }
        this.inProgress.next(false);
        timerSub?.unsubscribe();
      }, this.config.stepUpAnchor);
      timerSub = this.userTimeoutService.inactivityTimer('ecp-user-session').subscribe((ev) => {
        if (ev === 'warn' || ev === 'logout') {
          // if they just hang out here, we need to close this to show the logout warning
          // or to logout
          this.alloy.close();
        }
      });
    });
  }

  private checkAndSetIfSandboxMode(
    config: Init & {
      key: string;
      journeyToken: string;
      journeyApplicationToken?: string;
      socurePublicToken?: string;
      isNext?: boolean;
    }
  ): void {
    if (config.production) {
      // only need to check if we are actually in production
      if (this.lenderService.lenderData.alloySettings.sandboxMode) {
        config.production = false; // only thing that changes on client
      }
    }
  }

  /**
   * If Iovation TruValidate is enabled, this will go ahead and
   * inject the invasive tracking script to do its thing.
   */
  private trackWithIovation(): void {
    // not needed unless we implement 1st party Iovation TruValidate
  }

  /**
   * This will clear the script for Iovation TruValidate if it was being used.
   * Hopefully stopping tracking.
   */
  private stopTrackingWithIovation(): void {
    // todo: only needed if using 1st party Iovation TruValidate
  }
}

/**
 * Provides the information from the validation process to support the next steps in the flow.
 */
export interface AlloyVerificationResult {
  /** The status of the application start. */
  status: string;
  /** The journey application token from Alloy. */
  journeyApplicationId: string;
  /** The complete status as a aggregate of status and journeyStatus, provided by Alloy. */
  outcome?: string;
  /** This is the status of the journey overall */
  journeyStatus?: string;
  /** This application identity needs more information to verify */
  stepUpRequired: boolean;
  /** This application identity is verified and can go for review and post app. */
  actionRequired: AlloyStatus;
  /** If an error occurs, it will be described here. */
  errorMessage?: string;
  /** If an error occurs, it will have a status code here. */
  alloyErrorCode?: number;
  /** If an error occurs, it will have a specific error code here. */
  alloyMinorCode?: number;
  /** If an error occurs, additional specific error information will be here. */
  alloyErrorDetails?: { [key: string]: string };
  /** OAO/OLA ApplicationId */
  applicationId?: string;
  /** Applicant Name */
  applicantName?: string;
}

export enum AlloyStatus {
  /** The status likely has not been set.*/
  Unknown = 'Unknown',
  /** The application is running long and returned a running status,
   *  you need to call back to get an updated status.
   */
  Pending = 'Pending',
  /** The alloy journey verified the applicant,
   * no further action is required.
   */
  Verified = 'Verified',
  /** The alloy journey rejected the applicant as fraudulent.
   * Should be logged and not submitted.
   */
  Rejected = 'Rejected',
  /** The applicant could not be verified for some reason,
   *  there are a variety of reasons for this, but the action
   * needed is the same.
   */
  ManualReview = 'ManualReview',
  /** The applicant needs to provide some additional information
   *  to verify, which can be done in the application via an
   *  alloy step up. Action from there can be handled client-side.
   */
  StepUp = 'StepUp',
  /** There was an error, this can be treated as manual review. */
  Error = 'Error'
}
